/**
 * @fileoverview Shared rewards lists — ONE CARD PER COMPANY, each with
 * subsections for what that wash offers the customer: Credits (gift credit
 * remaining there), Loyalty (points balance + tap-to-expand redemption
 * options), and Gifts & Promos (active vouchers). Credits and points are
 * owner-scoped, so there is deliberately no cross-company total.
 *
 * Used by the customer Rewards tab and, titled "Rewards at Other Washes",
 * at the bottom of the owner Wash tab (owners hold rewards as customers of
 * other washes but have no /user/* shell).
 */
import React, { useCallback, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useFocusEffect } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api, apiPost } from '../api/client';
import Card from './Card';
import Button from './Button';
import { useToast } from './Toast';
import { useTheme } from '../context/ThemeContext';
import { spacing, fontSize, fontWeight, borderRadius, type ColorTheme } from '../utils/theme';

export interface CreditRow {
  id: string;
  ownerId: string;
  ownerName: string;
  balanceCents: number;
  expiresAt: string | null;
  senderName: string | null;
  senderMessage: string | null;
}

/** Exactly one of tierName / freeMinutes / vacMinutes / vendCents is set per row. */
interface RedemptionEntry {
  points: number;
  tierName: string | null;
  freeMinutes: number | null;
  vacMinutes?: number | null;
  vendCents?: number | null;
}

/** Human label for a loyalty redemption row. */
function redemptionLabel(entry: RedemptionEntry): string {
  if (entry.vacMinutes) return `${entry.vacMinutes} vacuum minutes`;
  if (entry.vendCents) return `$${(entry.vendCents / 100).toFixed(2)} vending credit`;
  if (entry.freeMinutes) return `${entry.freeMinutes} free self-serve minutes`;
  return `Free ${entry.tierName ?? ''} wash`.replace('  ', ' ');
}

export interface PointsRow {
  ownerId: string;
  ownerName: string;
  programName: string;
  balance: number;
  lifetimeEarned: number;
  redemptionTable: RedemptionEntry[];
}

export interface VoucherRow {
  id: string;
  ownerId?: string;
  code: string;
  kind: 'gift' | 'promo' | 'points_redemption';
  discountKind: 'percent' | 'free_wash' | 'free_minutes' | 'free_vend' | 'credit';
  percentOff: number | null;
  tierName: string | null;
  freeMinutes: number | null;
  freeVendCents?: number | null;
  /** Defensive: credit vouchers deposit at claim and are excluded from /api/me/vouchers. */
  creditCents?: number | null;
  /** Server-rendered scope ("Deluxe, Ultimate + Vacuums + Vending"); null on legacy rows. */
  appliesToLabel?: string | null;
  expiresAt: string | null;
  ownerName: string;
  locationName: string | null;
}

/** Headline for an active voucher row. */
function voucherTitle(v: VoucherRow): string {
  if (v.discountKind === 'credit') return v.creditCents != null ? `$${(v.creditCents / 100).toFixed(2)} credit` : 'Gift credit';
  if (v.discountKind === 'free_vend') return `$${((v.freeVendCents ?? 0) / 100).toFixed(2)} vending credit`;
  if (v.discountKind === 'free_minutes') return `${v.freeMinutes} free minutes`;
  if (v.discountKind === 'free_wash' || (v.discountKind === 'percent' && v.percentOff === 100)) {
    return v.tierName ? `Free ${v.tierName} wash` : 'Free wash';
  }
  return `${v.percentOff}% off${v.tierName ? ` ${v.tierName}` : ''}`;
}

interface Props {
  /** Section heading shown above the company cards. */
  title?: string;
  /** Render nothing at all when there are no credits/points/vouchers. */
  hideWhenEmpty?: boolean;
  /** Called after each fetch with the loaded (post-filter) data, for empty states above. */
  onLoaded?: (d: { credits: CreditRow[]; points: PointsRow[]; vouchers: VoucherRow[] }) => void;
  /** Toast on partial fetch failure (suppressed when offline upstream). */
  warnOnError?: boolean;
  /** Drop rewards from this owner — the owner wash tab hides self-issued rewards. */
  excludeOwnerId?: string;
}

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

interface CompanyGroup {
  ownerId: string;
  ownerName: string;
  credit: CreditRow | null;
  points: PointsRow | null;
  vouchers: VoucherRow[];
}

export default function RewardsLists({ title, hideWhenEmpty, onLoaded, warnOnError, excludeOwnerId }: Props) {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const toast = useToast();

  const [groups, setGroups] = useState<CompanyGroup[]>([]);
  const [expandedOwner, setExpandedOwner] = useState<string | null>(null);
  const [redeeming, setRedeeming] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    let failures = 0;
    const track = <T,>(p: Promise<T>, fallback: T): Promise<T> => p.catch(() => { failures++; return fallback; });
    let [c, v, p] = await Promise.all([
      track(api<{ credits: CreditRow[] }>('/api/me/wash-credit').then(r => r.credits), [] as CreditRow[]),
      track(api<{ vouchers: VoucherRow[] }>('/api/me/vouchers?all=1').then(r => r.vouchers), [] as VoucherRow[]),
      track(api<{ balances: PointsRow[] }>('/api/me/points').then(r => r.balances), [] as PointsRow[]),
    ]);
    if (excludeOwnerId) {
      c = c.filter(x => x.ownerId !== excludeOwnerId);
      v = v.filter(x => (x.ownerId ?? undefined) !== excludeOwnerId);
      p = p.filter(x => x.ownerId !== excludeOwnerId);
    }

    // Group by company. Vouchers may predate the ownerId field; fall back to
    // matching by ownerName so they still land in the right card.
    const byOwner = new Map<string, CompanyGroup>();
    const ensure = (ownerId: string, ownerName: string) => {
      let g = byOwner.get(ownerId);
      if (!g) { g = { ownerId, ownerName, credit: null, points: null, vouchers: [] }; byOwner.set(ownerId, g); }
      return g;
    };
    for (const row of c) ensure(row.ownerId, row.ownerName).credit = row;
    for (const row of p) ensure(row.ownerId, row.ownerName).points = row;
    for (const row of v) {
      const byId = row.ownerId ? byOwner.get(row.ownerId) : undefined;
      const byName = byId ?? [...byOwner.values()].find(g => g.ownerName === row.ownerName);
      (byName ?? ensure(row.ownerId ?? `name:${row.ownerName}`, row.ownerName)).vouchers.push(row);
    }
    const sorted = [...byOwner.values()].sort((a, b) =>
      (b.credit?.balanceCents ?? 0) - (a.credit?.balanceCents ?? 0)
      || (b.points?.balance ?? 0) - (a.points?.balance ?? 0)
      || a.ownerName.localeCompare(b.ownerName));
    setGroups(sorted);

    onLoaded?.({ credits: c, points: p, vouchers: v });
    if (failures > 0 && warnOnError) {
      toast.show({ type: 'warning', title: 'Some rewards didn’t load', message: 'Pull to refresh to try again.' });
    }
  }, [onLoaded, warnOnError, excludeOwnerId]);

  useFocusEffect(useCallback(() => { fetchAll(); }, [fetchAll]));

  const redeem = async (ownerId: string, idx: number, entry: RedemptionEntry) => {
    const key = `${ownerId}:${idx}`;
    setRedeeming(key);
    try {
      await apiPost('/api/points/redeem', { ownerId, redemptionIndex: idx });
      toast.show({
        type: 'success', title: 'Points redeemed!',
        message: `Your ${redemptionLabel(entry)} voucher is under Gifts & Promos — it applies at the pay screen.`,
      });
      fetchAll();
    } catch (err: any) {
      toast.show({ type: 'error', title: 'Could not redeem', message: err.message });
    } finally { setRedeeming(null); }
  };

  if (groups.length === 0 && hideWhenEmpty) return null;

  return (
    <View>
      {title && groups.length > 0 ? <Text style={styles.blockTitle}>{title}</Text> : null}

      {groups.map(g => {
        const pointsOpen = expandedOwner === g.ownerId;
        return (
          <Card key={g.ownerId} style={styles.companyCard}>
            <View style={styles.companyHeader}>
              <Ionicons name="business" size={18} color={colors.primary} />
              <Text style={styles.companyName}>{g.ownerName}</Text>
            </View>

            {/* ── Credits ── */}
            {g.credit && (
              <View style={styles.section}>
                <Text style={styles.subheader}>Credits</Text>
                <View style={styles.rowLine}>
                  <View style={{ flex: 1 }}>
                    {g.credit.senderName && (
                      <Text style={styles.rowFrom}>
                        <Ionicons name="gift" size={12} color={colors.primary} /> Gift from {g.credit.senderName}
                      </Text>
                    )}
                    {g.credit.senderMessage && (
                      <Text style={styles.rowMessage} numberOfLines={2}>“{g.credit.senderMessage}”</Text>
                    )}
                    {g.credit.expiresAt && <Text style={styles.rowSub}>Expires {fmtDate(g.credit.expiresAt)}</Text>}
                  </View>
                  <View style={{ alignItems: 'flex-end' }}>
                    <Text style={styles.rowAmount}>${(g.credit.balanceCents / 100).toFixed(2)}</Text>
                    <Text style={styles.rowSub}>remaining</Text>
                  </View>
                </View>
              </View>
            )}

            {/* ── Loyalty ── */}
            {g.points && (
              <View style={styles.section}>
                <Text style={styles.subheader}>Loyalty</Text>
                <TouchableOpacity style={styles.rowLine} onPress={() => setExpandedOwner(pointsOpen ? null : g.ownerId)}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.rowTitle}>{g.points.programName}</Text>
                    <Text style={styles.rowSub}>Lifetime: {g.points.lifetimeEarned} pts · tap to spend</Text>
                  </View>
                  <Text style={styles.rowAmount}>{g.points.balance} pts</Text>
                  <Ionicons name={pointsOpen ? 'chevron-up' : 'chevron-down'} size={16} color={colors.textLight} style={{ marginLeft: spacing.xs }} />
                </TouchableOpacity>
                {pointsOpen && (
                  <View style={styles.redeemBox}>
                    {g.points.redemptionTable.length === 0 ? (
                      <Text style={styles.rowSub}>No redemption options yet — keep earning!</Text>
                    ) : g.points.redemptionTable.map((entry, idx) => {
                      const affordable = g.points!.balance >= entry.points;
                      const label = redemptionLabel(entry);
                      return (
                        <View key={idx} style={styles.redeemRow}>
                          <View style={{ flex: 1 }}>
                            <Text style={styles.rowTitle}>{label}</Text>
                            <Text style={styles.rowSub}>{entry.points} pts</Text>
                          </View>
                          <Button
                            title={redeeming === `${g.ownerId}:${idx}` ? 'Redeeming…' : 'Redeem'}
                            size="sm"
                            variant={affordable ? 'primary' : 'outline'}
                            disabled={!affordable || redeeming !== null}
                            onPress={() => redeem(g.ownerId, idx, entry)}
                          />
                        </View>
                      );
                    })}
                  </View>
                )}
              </View>
            )}

            {/* ── Gifts & Promos ── */}
            {g.vouchers.length > 0 && (
              <View style={styles.section}>
                <Text style={styles.subheader}>Gifts & Promos</Text>
                {g.vouchers.map(v => (
                  <View key={v.id} style={styles.rowLine}>
                    <Ionicons name="gift" size={20} color={colors.primary} style={{ marginRight: spacing.sm }} />
                    <View style={{ flex: 1 }}>
                      <Text style={styles.rowTitle}>{voucherTitle(v)}</Text>
                      <Text style={styles.rowSub}>
                        {v.appliesToLabel ? `${v.appliesToLabel} · ` : ''}{v.locationName ? `${v.locationName} · ` : ''}1 use{v.expiresAt ? ` · expires ${fmtDate(v.expiresAt)}` : ''}
                      </Text>
                    </View>
                  </View>
                ))}
              </View>
            )}
          </Card>
        );
      })}
    </View>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  blockTitle: { fontSize: fontSize.md, fontWeight: fontWeight.semibold as any, color: c.text, marginTop: spacing.lg, marginBottom: spacing.sm },
  companyCard: { padding: spacing.md, marginBottom: spacing.md },
  companyHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.xs, paddingBottom: spacing.sm, borderBottomWidth: 1, borderBottomColor: c.borderLight },
  companyName: { fontSize: fontSize.md, fontWeight: fontWeight.bold as any, color: c.text },
  section: { marginTop: spacing.sm },
  subheader: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold as any, color: c.textSecondary, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 },
  rowLine: { flexDirection: 'row', alignItems: 'center', paddingVertical: spacing.xs },
  rowTitle: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold as any, color: c.text },
  rowSub: { fontSize: fontSize.xs, color: c.textSecondary, marginTop: 2 },
  rowFrom: { fontSize: fontSize.xs, color: c.primary, fontWeight: fontWeight.medium as any },
  rowMessage: { fontSize: fontSize.xs, color: c.textSecondary, fontStyle: 'italic', marginTop: 2 },
  rowAmount: { fontSize: fontSize.lg, fontWeight: fontWeight.bold as any, color: c.primary },
  redeemBox: { marginTop: spacing.xs, borderTopWidth: 1, borderTopColor: c.borderLight, paddingTop: spacing.xs, borderRadius: borderRadius.sm },
  redeemRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: spacing.xs, gap: spacing.sm },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
